import * as React from 'react';
import Text from '../text';

import CheckboxCategory from './checkbox-category';

import { CheckboxList as Props } from './form-content.checkbox-list.types';
import { SearchItem } from './form-content.types';
import { mod } from '../add-bem-modifiers';
import FormContentSearch from './form-content.search';

const CheckboxList: React.FC<React.PropsWithChildren<Props>> = props => {
  const [currentlySelectedValues, setCurrentlySelectedValues] = React.useState<
    Record<string, string[]>
  >({});
  const { content, emptySearchResultDescription, emptySearchResultText } =
    props;

  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResult, setSearchResult] = React.useState<SearchItem[]>();

  const updateCurrentlySelectedValues = (id: string, value: string) => {
    if (currentlySelectedValues[id]?.includes(value)) {
      setCurrentlySelectedValues(prevState => {
        const updatedValues = Object.entries(prevState).map(([id, values]) => [
          id,
          values.filter(option => option !== value),
        ]);
        return Object.fromEntries(updatedValues);
      });
    } else {
      setCurrentlySelectedValues(prevState => {
        const updatedValues = Object.entries(prevState).map(
          ([filterGroupId, values = []]) => {
            if (id === filterGroupId) {
              return [filterGroupId, [...values, value]];
            }

            return [filterGroupId, values];
          },
        );
        return Object.fromEntries(updatedValues);
      });
    }
  };

  const updateMultipleSelectedValues = (id: string, values: string[]) => {
    setCurrentlySelectedValues(prevState => ({
      ...prevState,
      [id]: values,
    }));
  };

  const removeAllValuesById = (id: string) => {
    setCurrentlySelectedValues(prevState => {
      const selectedValues = prevState[id] || [];
      const updatedValues = Object.entries(prevState).map(([id, values]) => [
        id,
        values.filter(option => !selectedValues.includes(option)),
      ]);
      return Object.fromEntries(updatedValues);
    });
  };

  React.useEffect(() => {
    const selectedValues = content
      .flatMap(({ categories }) => categories)
      .filter(category => category?.id)
      .map(category => [category?.id, category?.selectedValues]);
    setCurrentlySelectedValues(Object.fromEntries(selectedValues));
  }, [content]);

  return (
    <div>
      {'searchProps' in props ? (
        <FormContentSearch
          {...props.searchProps}
          searchResult={setSearchResult}
          searchTerm={setSearchTerm}
        />
      ) : undefined}

      {searchTerm.length > 0 && searchResult?.length === 0 && (
        <div className="form-content__search-no-results">
          <Text
            className="form-content__search-no-results-text"
            elementName="span"
            theme={Text.themes.emphasis}
          >
            {emptySearchResultText}
          </Text>
          <span>{emptySearchResultDescription}</span>
        </div>
      )}

      {content.map(({ categories = [], key, header }) => (
        <React.Fragment key={key}>
          {header && searchTerm.length === 0 ? (
            <Text
              className={mod('form-content__content-title', {
                hidden: searchTerm.length > 0,
              })}
              theme={Text.themes.emphasis}
            >
              {header}
            </Text>
          ) : undefined}

          {categories.map(category => (
            <CheckboxCategory
              key={category.id}
              searchResults={searchResult}
              isSearching={searchTerm.length > 0}
              currentlySelectedValues={currentlySelectedValues[category.id]}
              setSelectedValue={updateCurrentlySelectedValues}
              setMultipleSelectedValues={updateMultipleSelectedValues}
              removeAllValues={removeAllValuesById}
              {...category}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

export default CheckboxList;
